import { useRef, useState } from 'react';

import {
  Checkbox,
  Icon,
  MenuItem,
  Popover,
  type ConditionField,
  type TableSort,
} from '@/components/ui';
import type { ConditionTree } from '@/api/queries/workspace';
import { cn } from '@/utils/cn';

import { ContactsFilterDrawer } from './ContactsFilterDrawer';

export interface ColumnVisibilityEntry {
  id: string;
  label: string;
  visible: boolean;
  toggleable?: boolean;
}

export interface SortableField {
  id: string;
  label: string;
}

interface ToolbarProps {
  columns: ColumnVisibilityEntry[];
  onColumnToggle: (id: string) => void;
  sortableFields?: SortableField[];
  /** Current sort + setter — pairs with the same props on the Table. */
  sort?: TableSort | null;
  onSortChange?: (sort: TableSort | null) => void;
  /** Free-text quick search (name or email). */
  query: string;
  onQueryChange: (next: string) => void;
  /** The whole filter, as a condition tree (builder + power query). */
  conditions: ConditionTree;
  conditionFields: ConditionField[];
  onConditionsChange: (next: ConditionTree) => void;
}

export function ContactsToolbar({
  columns,
  onColumnToggle,
  sortableFields,
  sort,
  onSortChange,
  query,
  onQueryChange,
  conditions,
  conditionFields,
  onConditionsChange,
}: ToolbarProps) {
  const [filterOpen, setFilterOpen] = useState(false);
  const conditionCount = conditions.conditions?.length ?? 0;

  return (
    <div className="toolbar">
      <SearchBox value={query} onChange={onQueryChange} />

      <button
        type="button"
        className={cn('chip-btn', conditionCount > 0 && 'chip-btn-active')}
        onClick={() => setFilterOpen(true)}
      >
        <Icon name="filter" size={12} /> Filters
        {conditionCount > 0 && ` (${conditionCount})`}
      </button>

      {conditionCount > 0 && (
        <button
          type="button"
          className="chip-btn"
          onClick={() => onConditionsChange({ conjunction: conditions.conjunction, conditions: [] })}
        >
          Clear filters
          <Icon name="x" size={11} className="chip-x" />
        </button>
      )}

      <div className="toolbar-divider" />

      <ColumnsMenu columns={columns} onToggle={onColumnToggle} />

      {sortableFields && sortableFields.length > 0 && onSortChange && (
        <SortMenu fields={sortableFields} sort={sort ?? null} onChange={onSortChange} />
      )}

      <ContactsFilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        value={conditions}
        fields={conditionFields}
        onApply={onConditionsChange}
      />
    </div>
  );
}

function SearchBox({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [draft, setDraft] = useState(value);
  const [synced, setSynced] = useState(value);
  const debounceRef = useRef<number | null>(null);

  // Sync the draft when the committed value changes externally (e.g. cleared
  // filters) — done during render per React's derived-state guidance, not in an effect.
  if (value !== synced) {
    setSynced(value);
    setDraft(value);
  }

  const commit = (next: string) => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => onChange(next.trim()), 250);
  };

  return (
    <div className="search-input">
      <Icon name="search" size={13} className="si-icon" />
      <input
        value={draft}
        placeholder="Search name or email…"
        aria-label="Search contacts"
        onChange={(e) => {
          setDraft(e.target.value);
          commit(e.target.value);
        }}
      />
    </div>
  );
}

function SortMenu({
  fields,
  sort,
  onChange,
}: {
  fields: SortableField[];
  sort: TableSort | null;
  onChange: (next: TableSort | null) => void;
}) {
  const activeField = fields.find((f) => f.id === sort?.id);
  const label = activeField
    ? `Sort: ${activeField.label} ${sort?.direction === 'desc' ? '↓' : '↑'}`
    : 'Sort';
  return (
    <Popover
      placement="bottom-start"
      trigger={
        <button type="button" className={cn('chip-btn', sort && 'chip-btn-active')}>
          <Icon name="sortAsc" size={12} /> {label}
        </button>
      }
    >
      <div style={{ width: 220, maxHeight: 320, overflowY: 'auto' }}>
        <MenuItem onClick={() => onChange(null)}>
          <span style={{ flex: 1, fontWeight: sort ? 400 : 600 }}>None</span>
        </MenuItem>
        {fields.map((f) => {
          const active = sort?.id === f.id;
          return (
            <div key={f.id} style={{ display: 'flex' }}>
              <MenuItem
                onClick={() =>
                  onChange({
                    id: f.id,
                    direction: active && sort?.direction === 'asc' ? 'desc' : 'asc',
                  })
                }
              >
                <span style={{ flex: 1, fontWeight: active ? 600 : 400 }}>{f.label}</span>
                {active && (
                  <Icon
                    name={sort?.direction === 'desc' ? 'chevronDown' : 'chevronUp'}
                    size={11}
                  />
                )}
              </MenuItem>
            </div>
          );
        })}
      </div>
    </Popover>
  );
}

function ColumnsMenu({
  columns,
  onToggle,
}: {
  columns: ColumnVisibilityEntry[];
  onToggle: (id: string) => void;
}) {
  return (
    <Popover
      placement="bottom-start"
      closeOnClickInside={false}
      trigger={
        <button type="button" className="chip-btn">
          <Icon name="columns" size={12} /> Columns
        </button>
      }
    >
      <div style={{ width: 220, maxHeight: 320, overflowY: 'auto' }}>
        {columns.map((col) => (
          <MenuItem key={col.id} onClick={() => col.toggleable !== false && onToggle(col.id)}>
            <Checkbox
              checked={col.visible}
              disabled={col.toggleable === false}
              onChange={() => col.toggleable !== false && onToggle(col.id)}
              aria-label={`Toggle ${col.label}`}
            />
            <span style={{ flex: 1 }}>{col.label}</span>
          </MenuItem>
        ))}
      </div>
    </Popover>
  );
}

interface BulkBarProps {
  count: number;
  /** Overrides the "{count} selected" label, e.g. for set selections ("All 12,403 matching selected"). */
  scopeLabel?: string;
  onClear: () => void;
  onDelete?: () => void;
  onFlagReview?: () => void;
  onFlagAutoReview?: () => void;
  onStartAutoInvestigation?: () => void;
  onActivate?: () => void;
  onDeactivate?: () => void;
  onExport?: () => void;
  onAddTag?: () => void;
  onBulkChange?: () => void;
  exporting?: boolean;
}

export function BulkActionBar({
  count,
  scopeLabel,
  onClear,
  onDelete,
  onFlagReview,
  onFlagAutoReview,
  onStartAutoInvestigation,
  onActivate,
  onDeactivate,
  onExport,
  onAddTag,
  onBulkChange,
  exporting,
}: BulkBarProps) {
  return (
    <div className={cn('toolbar-bulk')} role="toolbar" aria-label="Bulk actions">
      <span className="tb-count">{scopeLabel ?? `${count} selected`}</span>
      {onBulkChange && (
        <button type="button" className="tb-action" onClick={onBulkChange}>
          <Icon name="edit" size={12} /> Bulk change
        </button>
      )}
      {onAddTag && (
        <button type="button" className="tb-action" onClick={onAddTag}>
          <Icon name="tags" size={12} /> Tag
        </button>
      )}
      {onFlagReview && (
        <button type="button" className="tb-action" onClick={onFlagReview}>
          <Icon name="shield" size={12} /> Flag for review
        </button>
      )}
      {onFlagAutoReview && (
        <button type="button" className="tb-action" onClick={onFlagAutoReview}>
          <Icon name="sparkles" size={12} /> Flag for auto review
        </button>
      )}
      {onStartAutoInvestigation && (
        <button type="button" className="tb-action" onClick={onStartAutoInvestigation}>
          <Icon name="search" size={12} /> Auto investigate
        </button>
      )}
      {onActivate && (
        <button type="button" className="tb-action" onClick={onActivate}>
          <Icon name="check" size={12} /> Activate
        </button>
      )}
      {onDeactivate && (
        <button type="button" className="tb-action" onClick={onDeactivate}>
          <Icon name="x" size={12} /> Deactivate
        </button>
      )}
      {onExport && (
        <button type="button" className="tb-action" onClick={onExport} disabled={exporting}>
          <Icon name="download" size={12} /> Export
        </button>
      )}
      {onDelete && (
        <button type="button" className="tb-action" onClick={onDelete}>
          <Icon name="trash" size={12} /> Delete
        </button>
      )}
      <button type="button" className="tb-close" onClick={onClear} aria-label="Clear selection">
        <Icon name="x" size={14} />
      </button>
    </div>
  );
}
